'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';

type LoginFormData = {
  email: string;
  password: string;
};

interface LoginGateProps {
  onLogin: (name: string) => void;
}

type LoginStatus = 'idle' | 'loading' | 'error';

export default function LoginGate({ onLogin }: LoginGateProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [loginStatus, setLoginStatus] = useState<LoginStatus>('idle');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({ mode: 'onBlur' });

  // BACKEND INTEGRATION POINT: POST /api/auth/login
  const onSubmit = async (data: LoginFormData) => {
    setLoginStatus('loading');
    await new Promise((r) => setTimeout(r, 1500));

    // Mock credential check
    if (data.email === 'priya.sharma@fcep.in' && data.password === 'FCEP@2026') {
      onLogin('Priya Sharma');
    } else {
      setLoginStatus('error');
    }
  };

  return (
    <div className="max-w-md mx-auto">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
        <Icon name="LockClosedIcon" size={18} variant="solid" className="text-primary flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-primary font-600 text-sm">Members-Only Section</p>
          <p className="text-blue-700 text-xs mt-1">
            Document verification is available only to registered FCEP members.
            Please login with your registered email and password.
          </p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 lg:p-8">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
            <Icon name="UserCircleIcon" size={32} variant="outline" className="text-primary" />
          </div>
          <h2 className="text-xl font-700 text-foreground">Member Login</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Sign in to access your document verification portal
          </p>
        </div>

        {loginStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-5 flex items-start gap-2">
            <Icon name="ExclamationCircleIcon" size={16} variant="solid" className="text-danger flex-shrink-0 mt-0.5" />
            <p className="text-danger text-sm">
              Invalid credentials — use the demo account below to sign in.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          <div>
            <label htmlFor="login-email" className="form-label">
              Registered Email <span className="text-danger">*</span>
            </label>
            <input
              id="login-email"
              type="email"
              placeholder="yourname@gmail.com"
              className={`input-field ${errors.email ? 'error' : ''}`}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address',
                },
              })}
            />
            {errors.email && (
              <p className="form-error">
                <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                {errors.email.message}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="login-password" className="form-label mb-0">
                Password <span className="text-danger">*</span>
              </label>
              <Link href="/registration-page" className="text-xs text-primary hover:underline font-500">
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                className={`input-field pr-10 ${errors.password ? 'error' : ''}`}
                {...register('password', { required: 'Password is required' })}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <Icon
                  name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'}
                  size={18}
                  variant="outline"
                />
              </button>
            </div>
            {errors.password && (
              <p className="form-error">
                <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                {errors.password.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loginStatus === 'loading'}
            className="btn-primary w-full justify-center text-base disabled:opacity-70 disabled:cursor-not-allowed"
            style={{ padding: '0.75rem 1.5rem' }}
          >
            {loginStatus === 'loading' ? (
              <>
                <Icon name="ArrowPathIcon" size={18} variant="outline" className="animate-spin" />
                Signing In...
              </>
            ) : (
              <>
                <Icon name="ArrowRightOnRectangleIcon" size={18} variant="outline" />
                Login to Member Portal
              </>
            )}
          </button>
        </form>

        {/* Demo Credentials */}
        <div className="mt-5 bg-muted border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="InformationCircleIcon" size={16} variant="solid" className="text-primary" />
            <p className="text-sm font-600 text-foreground">Demo Account</p>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Email:</span>
              <span className="font-500 text-foreground text-xs font-mono">priya.sharma@fcep.in</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Password:</span>
              <span className="font-500 text-foreground text-xs font-mono">FCEP@2026</span>
            </div>
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-4">
          Not a member yet?{' '}
          <Link href="/registration-page" className="text-primary font-600 hover:underline">
            Register for free
          </Link>
        </p>
      </div>
    </div>
  );
}